import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Activity, TrendingUp, TrendingDown, RefreshCw, AlertTriangle, CheckCircle, AlertCircle, Clock, Zap, Eye, Users, Shield, ArrowUpRight, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { LineChart, Line, ResponsiveContainer, Tooltip, Area, AreaChart, XAxis } from 'recharts';

interface RiskPulseProps {
  siteId?: string | null;
  companyId?: string | null;
}

interface PulseData {
  status: 'green' | 'amber' | 'red';
  score: number;
  change_factors: string[];
  suggested_actions: string[];
  trend?: 'up' | 'down' | 'stable';
}

export const RiskPulseWidget = ({ siteId, companyId }: RiskPulseProps) => {
  const [pulse, setPulse] = useState<PulseData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [trendData, setTrendData] = useState<{ day: string; score: number; label: string }[]>([]);
  const [daysSinceIncident, setDaysSinceIncident] = useState<number | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!companyId && !siteId) return;
    fetchPulse();
    fetchTrendData();
    fetchLastIncident();
  }, [siteId, companyId]);

  const fetchPulse = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setIsLoading(false);
        return;
      }
      const { data, error } = await supabase.functions.invoke('calculate-risk-pulse', {
        body: { siteId, companyId }
      });

      if (error) throw error;
      setPulse(data);
    } catch (error) {
      console.error('Error fetching risk pulse:', error);
      setPulse({
        status: 'green',
        score: 0.15,
        change_factors: ['Unable to calculate - insufficient data'],
        suggested_actions: ['Continue monitoring safety metrics'],
        trend: 'stable'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTrendData = async () => {
    try {
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const fullDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const mockData = days.map((day, i) => ({
        day,
        label: fullDays[i],
        score: Math.random() * 0.3 + 0.1
      }));
      setTrendData(mockData);
    } catch (error) {
      console.error('Error fetching trend data:', error);
    }
  };

  const fetchLastIncident = async () => {
    try {
      let query = supabase
        .from('peer_reports')
        .select('created_at')
        .in('risk_rating', ['high', 'very_high'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (siteId) {
        query = query.eq('site_id', siteId);
      }

      const { data } = await query;
      if (data && data.length > 0) {
        const lastDate = new Date(data[0].created_at);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - lastDate.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        setDaysSinceIncident(diffDays);
      } else {
        setDaysSinceIncident(30);
      }
    } catch (error) {
      console.error('Error fetching last incident:', error);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'green': 
        return { 
          gradient: 'from-emerald-500 via-emerald-500 to-teal-500',
          bgLight: 'bg-emerald-50 dark:bg-emerald-950/30',
          borderColor: 'border-emerald-200 dark:border-emerald-800',
          icon: CheckCircle,
          label: 'Healthy',
          description: 'Safety metrics within optimal range',
          textColor: 'text-emerald-600 dark:text-emerald-400',
          chartColor: '#10b981'
        };
      case 'amber': 
        return { 
          gradient: 'from-amber-500 via-orange-500 to-orange-500',
          bgLight: 'bg-amber-50 dark:bg-amber-950/30',
          borderColor: 'border-amber-200 dark:border-amber-800',
          icon: AlertCircle,
          label: 'Caution',
          description: 'Elevated risk indicators detected',
          textColor: 'text-amber-600 dark:text-amber-400',
          chartColor: '#f59e0b'
        };
      case 'red': 
        return { 
          gradient: 'from-red-500 via-rose-500 to-pink-500',
          bgLight: 'bg-red-50 dark:bg-red-950/30',
          borderColor: 'border-red-200 dark:border-red-800',
          icon: AlertTriangle,
          label: 'At Risk',
          description: 'Critical safety concerns require attention',
          textColor: 'text-red-600 dark:text-red-400',
          chartColor: '#ef4444'
        };
      default: 
        return { 
          gradient: 'from-slate-500 to-slate-600',
          bgLight: 'bg-slate-50 dark:bg-slate-900',
          borderColor: 'border-slate-200 dark:border-slate-700',
          icon: Activity,
          label: 'Unknown',
          description: 'Unable to determine status',
          textColor: 'text-slate-600 dark:text-slate-400',
          chartColor: '#64748b'
        };
    }
  };

  if (isLoading && !pulse) {
    return (
      <div className="space-y-4">
        <div className="h-32 rounded-2xl bg-muted/50 animate-pulse" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-24 rounded-xl bg-muted/50 animate-pulse" />
          <div className="h-24 rounded-xl bg-muted/50 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!pulse) return null;

  const config = getStatusConfig(pulse.status);
  const StatusIcon = config.icon;
  const scorePercent = Math.round(pulse.score * 100);

  return (
    <div className="space-y-6">
      {/* Hero Status Card */}
      <div className={cn(
        "relative overflow-hidden rounded-2xl p-6 text-white",
        `bg-gradient-to-br ${config.gradient}`
      )}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>

        {/* Animated pulse ring */}
        <div className="absolute top-6 right-6">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-white/30 animate-ping" style={{ animationDuration: '2s' }} />
            <div className="relative w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <StatusIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-4 w-4 opacity-80" />
            <span className="text-sm font-medium opacity-80 uppercase tracking-wider">Organizational Risk Pulse™</span>
          </div>
          
          <h2 className="text-4xl font-bold mb-1">{config.label}</h2>
          <p className="text-white/80 text-sm mb-6">{config.description}</p>

          <div className="flex items-end gap-6">
            <div>
              <div className="text-5xl font-bold tabular-nums">{scorePercent}%</div>
              <div className="text-sm opacity-80 mt-1">Risk Score</div>
            </div>
            
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm">
              {pulse.trend === 'up' && <TrendingUp className="h-4 w-4" />}
              {pulse.trend === 'down' && <TrendingDown className="h-4 w-4" />}
              {pulse.trend === 'stable' && <Activity className="h-4 w-4" />}
              <span className="text-sm font-medium">
                {pulse.trend === 'up' ? 'Trending Up' : pulse.trend === 'down' ? 'Improving' : 'Stable'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* 7-Day Trend */}
        <Card className={cn("border", config.borderColor)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">7-Day Trend</span>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={fetchPulse} 
                disabled={isLoading}
                className="h-7 w-7"
              >
                <RefreshCw className={cn("h-3.5 w-3.5", isLoading && "animate-spin")} />
              </Button>
            </div>
            <div className="h-20">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={config.chartColor} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={config.chartColor} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload?.[0]) {
                        return (
                          <div className="bg-popover text-popover-foreground text-xs px-3 py-2 rounded-lg shadow-lg border">
                            <div className="font-medium">{payload[0].payload.label}</div>
                            <div className={config.textColor}>{(Number(payload[0].value) * 100).toFixed(0)}% risk</div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                  <Area 
                    type="monotone" 
                    dataKey="score" 
                    stroke={config.chartColor}
                    strokeWidth={2}
                    fill="url(#colorScore)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Days Since Incident */}
        <Card className={cn("border", config.borderColor)}>
          <CardContent className="p-4 flex flex-col items-center justify-center h-full">
            <div className={cn(
              "w-14 h-14 rounded-full flex items-center justify-center mb-2",
              config.bgLight
            )}>
              <Clock className={cn("h-6 w-6", config.textColor)} />
            </div>
            <div className="text-3xl font-bold tabular-nums">{daysSinceIncident ?? '-'}</div>
            <div className="text-xs text-muted-foreground text-center">Days Since High Risk Incident</div>
          </CardContent>
        </Card>
      </div>

      {/* Contributing Factors */}
      {pulse.change_factors.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Contributing Factors
            </h4>
            <div className="flex flex-wrap gap-2">
              {pulse.change_factors.map((factor, i) => (
                <Badge 
                  key={i} 
                  variant="secondary" 
                  className={cn(
                    "text-xs font-normal",
                    i === 0 && pulse.status !== 'green' && config.bgLight,
                    i === 0 && pulse.status !== 'green' && config.textColor
                  )}
                >
                  {factor}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Suggested Actions */}
      {pulse.suggested_actions.length > 0 && (
        <Card className={cn("border-2", config.borderColor)}>
          <CardContent className="p-4">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Zap className={cn("h-4 w-4", config.textColor)} />
              Recommended Actions
            </h4>
            <div className="space-y-2">
              {pulse.suggested_actions.map((action, i) => (
                <div 
                  key={i} 
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer",
                    config.bgLight,
                    "hover:opacity-80"
                  )}
                >
                  <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold text-white",
                    `bg-gradient-to-br ${config.gradient}`
                  )}>
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">{action}</p>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RiskPulseWidget;